import { useEffect, useRef } from "react"
import { useLocation } from "react-router"

import { USER_ACCOUNT_LOADING_STATUS } from "../../ClientAuthorization/Default/common/constant"

import { validateEmail, isEmptyString } from "utils/helpers"
import { useBookingContext } from "modules/Booking/BookingContext"
import { AfterFooter } from "components"
import { Analytics } from "utils/analytics"
import { useGetServicePrice } from "modules/Booking/common/hooks"
import { getOnlineOrOffline } from "modules/BookingCommon/common/utils"
import { SESSION_TYPE } from "modules/Booking/common/constants"
import { useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { UserInfoWithForm } from "modules/Booking/FormPage/components/UserInfoWithForm"
import { PaymentMethodsBlock } from "modules/Booking/FormPage/components/PaymentMethodsBlock"
import { UserDetailsForm } from "modules/Booking/FormPage/components/UserDetailsForm"
import {
    setEmailError,
    setNameError,
    setPhoneError,
    setShowEmailError,
    setShowErrors,
    focusOnField,
} from "modules/Booking/FormPage/common"
import {
    Deposit,
    DepositBannerNext,
    DepositBannerRegular,
    NextPageButtonWithAdditionalInfoPay,
    SessionInfoCard,
    SessionInfoMobileCard,
    SingleSession,
} from "modules/Booking/FormPage/components"
import { FormAcceptation } from "modules/Booking/FormPage/components/FormAcceptation"
import {
    EMAIL_ERRORS_TEXT,
    NAME_ERRORS_TEXT,
    PHONE_ERRORS_TEXT,
} from "modules/Booking/FormPage/common/constants"
import { useFormPageContext } from "modules/Booking/FormPage/common/FormPageContext"
import { useUserFormErrors } from "modules/Booking/FormPage/common/hooks"
import { useOnlyFreeSessions } from "pages/FreePsysPage/useOnlyFreeSessions"
import { formatToFull } from "utils/datetime-format-utils"

export function FormPage() {
    const refPhoneLabel = useRef<HTMLDivElement | null>(null)
    const refEmailLabel = useRef<HTMLLabelElement | null>(null)
    const refNameLabel = useRef<HTMLLabelElement | null>(null)
    const refPhone = useRef<HTMLInputElement | null>(null)
    const refEmail = useRef<HTMLInputElement | null>(null)
    const refName = useRef<HTMLInputElement | null>(null)

    const [{ userAccount, loading: clientLoading }] = useClientContext()
    const onlyFree = useOnlyFreeSessions()
    const { key } = useLocation()
    const [{ form }, { dispatch }] = useFormPageContext()

    const [
        {
            psy,
            hasPreviousSessions,
            selectedTime,
            slotDuration,
            isOnline,
            serviceGroup,
            sessionType,
            deposits,
        },
    ] = useBookingContext()

    const userFormErrors = useUserFormErrors()

    const showUserDetailsForm = !hasPreviousSessions || !form.phone || !form.email || !form.name

    const [, discountPrice] = useGetServicePrice()

    useEffect(() => {
        Analytics.user_went_to_form({
            psy,
            customFields: {
                selectedSessionDateTime: formatToFull(selectedTime),
                selectedSessionFormat: getOnlineOrOffline(isOnline),
                selectedSessionType: serviceGroup?.name,
                selectedSessionDuration: slotDuration,
                selectedSessionPrice: discountPrice,
            },
        })
    }, [])

    useEffect(() => {
        dispatch(setPhoneError(isEmptyString(form.phone) ? PHONE_ERRORS_TEXT.empty : ""))
    }, [form.phone])

    useEffect(() => {
        if (isEmptyString(form.email)) {
            dispatch(setEmailError(EMAIL_ERRORS_TEXT.empty))
        } else if (!validateEmail(form.email)) {
            dispatch(setEmailError(EMAIL_ERRORS_TEXT.validation))
        } else {
            dispatch(setEmailError(undefined))
        }
    }, [form.email])

    useEffect(() => {
        if (isEmptyString(form.name)) {
            dispatch(setNameError(NAME_ERRORS_TEXT.empty))
        } else {
            dispatch(setNameError(""))
        }
    }, [form.name])

    function onBeforeApplyDiscount() {
        if (isEmptyString(form.email) || !validateEmail(form.email)) {
            dispatch(setShowEmailError(true))

            if (isEmptyString(form.email)) {
                dispatch(setEmailError(EMAIL_ERRORS_TEXT.emptyFromPromoCode))
            } else if (!validateEmail(form.email)) {
                dispatch(setEmailError(EMAIL_ERRORS_TEXT.validation))
            }

            focusOnField(refEmail, refEmailLabel)
            return false
        } else {
            dispatch(setShowEmailError(false))
            return true
        }
    }

    function handleShowErrors() {
        const errors = [
            {
                isError: !!form.phoneError,
                input: refPhone,
                label: refPhoneLabel,
            },
            {
                isError: !!form.emailError,
                input: refEmail,
                label: refEmailLabel,
            },
            {
                isError: !!form.nameError,
                input: refName,
                label: refNameLabel,
            },
        ]
        for (let i = 0; i < errors.length; i++) {
            const error = errors[i]
            if (!error.isError) continue
            if (focusOnField(error.input, error.label)) break
        }
        dispatch(setShowErrors(true))
    }

    const showDeposit =
        clientLoading === USER_ACCOUNT_LOADING_STATUS.READY && userAccount.allowed_deposit_purchase

    return (
        <div className="redesign-container">
            <div className="booking-form-container">
                <div className="payment-and-contact-data-block">
                    {(sessionType === SESSION_TYPE.DEFAULT ||
                        sessionType === SESSION_TYPE.DEPOSIT) &&
                        deposits &&
                        showDeposit &&
                        deposits.length > 0 && (
                            <>
                                <div className="booking-form__deposit">
                                    <SingleSession />
                                    <Deposit deposits={deposits} />
                                </div>
                                <hr className="mt-0 mb-0" />
                            </>
                        )}
                    {!onlyFree && (
                        <PaymentMethodsBlock
                            hasPreviousSessions={hasPreviousSessions}
                            onBeforeApplyDiscount={onBeforeApplyDiscount}
                        />
                    )}
                    {sessionType === SESSION_TYPE.DEPOSIT && (
                        <div className="booking-form__depositBannerMobile">
                            <DepositBannerRegular />
                            <DepositBannerNext />
                        </div>
                    )}
                    <div className="booking-form">
                        {(showUserDetailsForm && (
                            <>
                                <UserDetailsForm
                                    refPhoneLabel={refPhoneLabel}
                                    refEmailLabel={refEmailLabel}
                                    refNameLabel={refNameLabel}
                                    refPhone={refPhone}
                                    refEmail={refEmail}
                                    refName={refName}
                                />
                                <FormAcceptation psy={psy} />
                            </>
                        )) || <UserInfoWithForm />}
                    </div>
                    <SessionInfoMobileCard />
                </div>
                <div>
                    <SessionInfoCard
                        formHasErrors={!!userFormErrors.length}
                        showErrors={handleShowErrors}
                        userData={form}
                    />
                    {sessionType === SESSION_TYPE.DEPOSIT && (
                        <div className="booking-form__depositBannerDesktop">
                            <DepositBannerRegular />
                            <DepositBannerNext />
                        </div>
                    )}
                </div>
            </div>
            <AfterFooter afterFooterKey={key}>
                <NextPageButtonWithAdditionalInfoPay
                    formHasErrors={!!userFormErrors.length}
                    showErrors={handleShowErrors}
                    userData={form}
                />
            </AfterFooter>
        </div>
    )
}
